import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlayCircle, BookOpen, ListChecks, HelpCircle, Sparkles, GitBranch } from "lucide-react";

export function HelpHomePage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  const t = (es: string, en: string) => (isEn ? en : es);

  const cards = [
    {
      to: "/help/tour",
      icon: <PlayCircle size={48} className="text-primary-500" />,
      title: t("Tour Animado", "Animated Tour"),
      desc: t(
        "Recorrido interactivo de 8 minutos por los módulos principales del sistema, con animaciones y avance automático.",
        "Interactive 8-minute walkthrough of the main system modules with animations and auto-advance."
      ),
      cta: t("Iniciar Tour", "Start Tour"),
    },
    {
      to: "/help/operations",
      icon: <ListChecks size={48} className="text-green-500" />,
      title: t("Operaciones Típicas", "Typical Operations"),
      desc: t(
        "Flujos paso a paso para las tareas más comunes: publicar comunicados, aprobar permisos, registrar pagos, gestionar reuniones.",
        "Step-by-step flows for the most common tasks: publishing announcements, approving permits, recording payments, managing meetings."
      ),
      cta: t("Ver Operaciones", "View Operations"),
    },
    {
      to: "/help/screens",
      icon: <BookOpen size={48} className="text-orange-500" />,
      title: t("Guía Pantalla por Pantalla", "Screen-by-Screen Guide"),
      desc: t(
        "Referencia completa de cada pantalla del sistema: propósito, acciones disponibles, roles requeridos y consejos.",
        "Complete reference for every screen in the system: purpose, available actions, required roles, and tips."
      ),
      cta: t("Explorar Pantallas", "Explore Screens"),
    },
    {
      to: "/flujos",
      icon: <GitBranch size={48} className="text-purple-500" />,
      title: t("Flujos del Sistema", "System Flows"),
      desc: t(
        "El recorrido completo de cada rol, de principio a fin: dónde empieza, quién decide qué, y dónde termina. Cada paso enlaza a la pantalla real.",
        "Each role's flow from start to finish: where it begins, who decides what, and where it ends. Every step links to the real screen."
      ),
      cta: t("Ver Flujos", "View Flows"),
    },
  ];

  return (
    <div className="space-y-8">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 mb-4">
          <HelpCircle size={32} className="text-primary" />
        </div>
        <h1 className="text-3xl font-bold">
          {t("Centro de Ayuda", "Help Center")}
        </h1>
        <p className="text-slate-600 mt-2">
          {t(
            "Bienvenido al portal del Condominio Costa Dorada I. Elija una opción para aprender a usar el sistema.",
            "Welcome to the Costa Dorada I portal. Pick an option below to learn how to use the system."
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="card hover:shadow-lg transition group flex flex-col"
          >
            <div className="mb-4">{c.icon}</div>
            <h2 className="text-xl font-semibold mb-2">{c.title}</h2>
            <p className="text-sm text-slate-600 flex-1">{c.desc}</p>
            <div className="mt-4 text-primary font-medium text-sm group-hover:underline inline-flex items-center gap-1">
              {c.cta} →
            </div>
          </Link>
        ))}
      </div>

      <div className="card bg-gradient-to-br from-primary-50 to-white">
        <div className="flex items-start gap-4">
          <Sparkles className="text-accent shrink-0 mt-1" size={24} />
          <div>
            <h3 className="font-semibold mb-1">
              {t("¿Necesita ayuda personalizada?", "Need personalized help?")}
            </h3>
            <p className="text-sm text-slate-600">
              {t(
                "Contacte directamente a la administración: ",
                "Contact administration directly: "
              )}
              <a href="mailto:admin@costadorada1.com" className="text-primary hover:underline">
                admin@costadorada1.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
